/**
 * Client mirror of the server's read rule for the per-employee lists on EmployeeDetail
 * (GET /api/appraisals/employee/:id/history and GET /api/documents/employee/:id/list),
 * so the page never asks for a list the server is sure to refuse. A refusal still costs a
 * round trip and five console errors (the browser's own line plus the axios interceptor's).
 * The server stays the authority: the page still hides a section on any error.
 *
 * Server side, in order:
 * - the route admits only APP_READ / DOC_READ (server/constants/permissions.js);
 * - resolveScope (server/middleware/policy.js) picks ONE scope per resource, widest first;
 * - canAccessResource judges the employee under that scope alone.
 * So a wider grant decides even where a narrower one would have matched: a department
 * manager is refused a direct report's history when the report sits in another department,
 * because read_department outranks read_reports.
 *
 * Each helper takes AuthContext's `hasPermission(resource, action)` and `user`, whose
 * employee_id and department_id are the users-row values the server reads.
 */

const ROUTE_GRANTS = {
  appraisals: ['read_all', 'read_department', 'read_own', 'read_reports'],
  documents: ['read_all', 'read_own', 'read_shared'],
};

const holds = (hasPermission, resource, actions) => actions.some((action) => hasPermission(resource, action));
// A null on either side never matches, unlike String(null) === String(null).
const same = (a, b) => a != null && b != null && String(a) === String(b);

/**
 * resolveScope's answer for this caller and resource.
 * @returns {'all'|'department'|'reports'|'own'|'none'}
 */
export const resolveReadScope = (hasPermission, user, resource) => {
  if (holds(hasPermission, resource, ['read_all', 'update_all', 'delete'])) return 'all';
  if (holds(hasPermission, resource, ['read_department', 'update_department']) && user?.department_id) return 'department';
  if (holds(hasPermission, resource, ['read_reports', 'update_reports']) && user?.employee_id) return 'reports';
  if (holds(hasPermission, resource, ['read_own', 'update_own', 'delete_own'])) return 'own';
  return 'none';
};

/**
 * Whether the server will hand this caller this employee's list.
 *
 * @param {Function} hasPermission AuthContext's hasPermission.
 * @param {{ employee_id?: number|null, department_id?: number|null }|null} user The signed-in user.
 * @param {'appraisals'|'documents'} resource
 * @param {{ id: number|string, department_id?: number|null, manager_id?: number|null }|null} employee
 *   The page's employee. Before it has loaded, pass `{ id }`: the department and reports
 *   scopes then answer false until the record says where the employee sits.
 * @returns {boolean}
 */
export const canReadEmployeeList = (hasPermission, user, resource, employee) => {
  if (!employee || !holds(hasPermission, resource, ROUTE_GRANTS[resource] || [])) return false;
  switch (resolveReadScope(hasPermission, user, resource)) {
    case 'all':
      return true;
    case 'department':
      return same(employee.department_id, user?.department_id);
    case 'reports':
      // canAccessResource honours the reports scope for appraisals only.
      return resource === 'appraisals' && same(employee.manager_id, user?.employee_id);
    case 'own':
      return same(employee.id, user?.employee_id);
    default:
      return false;
  }
};
